import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, RotateCcw } from 'lucide-react';

/**
 * Card com os dados do sistema (fluxo teórico)
 */
export default function CardFluxoSistema({
  initialValue,
  onInitialValueChange,
  receivedCash,
  accumulatedCash,
  expectedFinal,
  onReset
}) {
  // Inicializar com valor formatado
  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const numValue = Number(val);
    if (isNaN(numValue)) return '';
    const rounded = Math.round(numValue * 100) / 100;
    return rounded.toFixed(2);
  };

  const [localValue, setLocalValue] = useState(() => formatValue(initialValue));
  const [isFocused, setIsFocused] = useState(false);

  // Atualizar valor local quando initialValue muda externamente
  useEffect(() => {
    if (!isFocused) {
      const formatted = formatValue(initialValue);
      setLocalValue(formatted);
    }
  }, [initialValue, isFocused]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);
    
    // Converter para número e chamar callback
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      // Garantir que não há zeros a mais - arredondar para 2 casas
      const rounded = Math.round(numValue * 100) / 100;
      onInitialValueChange(rounded);
    } else if (inputValue === '' || inputValue === '-') {
      // Permitir campo vazio ou sinal negativo durante digitação
      onInitialValueChange(0);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Formatar com exatamente 2 casas decimais ao sair do campo
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue)) {
      const rounded = Math.round(numValue * 100) / 100;
      const formatted = rounded.toFixed(2);
      setLocalValue(formatted);
      onInitialValueChange(rounded);
    } else {
      setLocalValue('0.00');
      onInitialValueChange(0);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Quando focar, mostrar o valor numérico limpo (sem zeros extras)
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue)) {
      // Mostrar número arredondado, mas sem formatação forçada
      const rounded = Math.round(numValue * 100) / 100;
      setLocalValue(rounded.toString());
    }
  };

  return (
    <div className="p-10 rounded-[3.5rem] shadow-2xl border-4 bg-white border-green-50 space-y-8 relative overflow-hidden">
      <div className="flex justify-between items-center relative z-10">
        <h2 className="text-2xl font-black text-green-800 flex items-center gap-3">
          <Calculator className="text-green-600" /> Fluxo de Sistema
        </h2>
        <button 
          onClick={onReset} 
          className="text-slate-300 hover:text-green-600 transition-colors"
        >
          <RotateCcw size={20}/>
        </button>
      </div>
      
      <div className="space-y-6 relative z-10">
        <div className="bg-slate-100/50 p-6 rounded-3xl group transition-all focus-within:bg-white focus-within:shadow-inner">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-2">
            Fundo de Maneio (Abertura)
          </label>
          <div className="flex items-center gap-3">
            <DollarSign className="text-slate-300 group-focus-within:text-green-600" size={24}/>
            <input 
              type="number" 
              step="0.01"
              className="w-full bg-transparent text-3xl font-black outline-none text-slate-900" 
              value={localValue} 
              placeholder="0.00"
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-3xl border-2 border-blue-100 shadow-inner">
          <label className="block text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1 px-2">
            Dinheiro Acumulado
          </label>
          <p className="text-4xl font-black text-blue-700">{Number(accumulatedCash).toFixed(2)}€</p>
        </div>

        <div className="bg-emerald-50 p-6 rounded-3xl border-2 border-emerald-100 shadow-inner">
          <label className="block text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1 px-2">
            Total Recebido Hoje (Cash)
          </label>
          <p className="text-4xl font-black text-emerald-700">{Number(receivedCash).toFixed(2)}€</p>
        </div>
      </div>

      <div className="pt-8 border-t-4 border-dashed border-slate-100 flex flex-col items-center relative z-10">
        <p className="text-slate-400 font-black uppercase text-[10px] mb-2 tracking-widest">
          Saldo Teórico Esperado
        </p>
        <p className="text-7xl font-black text-slate-900 tracking-tighter">
          {Number(expectedFinal).toFixed(2)}€
        </p>
      </div>
    </div>
  );
}



